import { useCallback, useEffect, useMemo, useState } from 'react';
import { toWeekMoment, type Bundle, type WeekMoment } from '@tlv/shared';
import { loadBundle } from './data/loadBundle.js';
import { createScoringContext, scoreCells, topCells, type Conditions } from './scoring/score.js';
import { describeCell } from './scoring/describe.js';
import { GoogleHotspotMap, type MapFailure } from './components/GoogleHotspotMap.js';
import { MapUnavailable } from './components/MapUnavailable.js';
import { resolveApiKey } from './components/mapConfig.js';
import { TimeSlider } from './components/TimeSlider.js';
import { TopCells, CellDetail } from './components/CellPanel.js';
import { AboutSheet } from './components/AboutSheet.js';
import { loadForecast, weatherForDate, type HourlyWeather, type WeatherState } from './modifiers/weather.js';
import { loadCalendar, multiplierForInstant, type RestWindow } from './modifiers/hebcal.js';
import { DEMAND_RAMP } from './theme.js';

const SEEN_ABOUT_KEY = 'tlv-hotspots:seen-about';
const TOP_COUNT = 5;

/**
 * The absolute date corresponding to a day/time within the current week.
 *
 * The slider scrubs a position in the week rather than a calendar date, but weather and
 * the Hebrew calendar are keyed to real instants — so scrubbing to "Friday 21:00" has
 * to resolve to *this* Friday to look anything up.
 */
function dateForMoment(moment: WeekMoment, now: Date): Date {
  const result = new Date(now);
  result.setDate(now.getDate() + (moment.day - now.getDay()));
  result.setHours(Math.floor(moment.minutesOfDay / 60), moment.minutesOfDay % 60, 0, 0);
  return result;
}

export function App(): JSX.Element {
  const [bundle, setBundle] = useState<Bundle | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [now, setNow] = useState(() => new Date());
  const [moment, setMoment] = useState<WeekMoment | null>(null);
  const [isLive, setIsLive] = useState(true);

  const [selected, setSelected] = useState<string | null>(null);
  const [showAbout, setShowAbout] = useState(false);

  // The map is optional infrastructure: without a key, or without a connection, the
  // model and the ranked list are unaffected.
  const apiKey = useMemo(() => resolveApiKey(), []);
  const [mapFailure, setMapFailure] = useState<MapFailure | null>(null);
  const mapUsable = apiKey !== null && mapFailure === null;

  const [forecast, setForecast] = useState<HourlyWeather | null>(null);
  const [forecastSource, setForecastSource] = useState<WeatherState['source']>('none');
  const [restWindows, setRestWindows] = useState<RestWindow[]>([]);

  // Load the dataset.
  useEffect(() => {
    loadBundle()
      .then(({ bundle: loaded }) => {
        setBundle(loaded);
        if (!localStorage.getItem(SEEN_ABOUT_KEY)) setShowAbout(true);
      })
      .catch((error: unknown) => {
        setLoadError(error instanceof Error ? error.message : String(error));
      });
  }, []);

  // Tick the clock. One minute is plenty — the model moves on half-hour boundaries.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Conditions. Both are best-effort: failure leaves the multipliers neutral.
  useEffect(() => {
    loadForecast().then(({ forecast: value, source }) => {
      setForecast(value);
      setForecastSource(source);
    });
    loadCalendar(new Date()).then(({ windows }) => setRestWindows(windows));
  }, []);

  const liveMoment = useMemo(() => toWeekMoment(now), [now]);
  const activeMoment = isLive ? liveMoment : (moment ?? liveMoment);

  const context = useMemo(() => (bundle ? createScoringContext(bundle) : null), [bundle]);

  const targetDate = useMemo(() => dateForMoment(activeMoment, now), [activeMoment, now]);

  const weather = useMemo(
    () => (forecast ? weatherForDate(forecast, targetDate, forecastSource) : null),
    [forecast, targetDate, forecastSource],
  );

  const calendar = useMemo(
    () => multiplierForInstant(restWindows, targetDate.getTime()),
    [restWindows, targetDate],
  );

  const conditions: Conditions = useMemo(
    () => ({
      weatherMultiplier: weather?.multiplier ?? 1,
      shabbatMultiplier: calendar.multiplier,
    }),
    [weather, calendar],
  );

  const scored = useMemo(
    () => (context ? scoreCells(context, activeMoment, conditions) : []),
    [context, activeMoment, conditions],
  );

  const best = useMemo(() => topCells(scored, TOP_COUNT), [scored]);

  const describe = useCallback(
    (h3: string) => (context ? describeCell(context, h3, activeMoment) : { landmarks: [], openVenues: 0, guessedShare: 0 }),
    [context, activeMoment],
  );

  const selectedCell = selected ? scored.find((cell) => cell.h3 === selected) ?? null : null;

  const handleMomentChange = useCallback((next: WeekMoment) => {
    setMoment(next);
    setIsLive(false);
  }, []);

  const handleGoLive = useCallback(() => {
    setIsLive(true);
    setMoment(null);
    setNow(new Date());
  }, []);

  const dismissAbout = useCallback(() => {
    localStorage.setItem(SEEN_ABOUT_KEY, '1');
    setShowAbout(false);
  }, []);

  if (loadError) {
    return (
      <div className="fatal">
        <h1>No data</h1>
        <p>{loadError}</p>
      </div>
    );
  }

  if (!bundle || !context) {
    return (
      <div className="fatal">
        <p>Loading Tel Aviv…</p>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="map-region">
        {mapUsable ? (
          <GoogleHotspotMap
            apiKey={apiKey}
            cells={scored}
            selected={selected}
            onSelect={setSelected}
            bbox={bundle.meta.bbox}
            onFailure={setMapFailure}
          />
        ) : (
          <MapUnavailable reason={apiKey === null ? 'no-key' : (mapFailure ?? 'network')} />
        )}

        <header className="top-bar">
          <div className="condition-chips">
            {bundle.meta.synthetic && (
              <button
                type="button"
                className="chip chip-warning"
                onClick={() => setShowAbout(true)}
              >
                Synthetic data
              </button>
            )}
            {weather && <span className="chip">{weather.label}</span>}
            {!weather && !isLive && (
              <span className="chip chip-muted">No forecast this far out</span>
            )}
            {calendar.label && <span className="chip chip-accent">{calendar.label}</span>}
          </div>
          <button
            type="button"
            className="info-button"
            onClick={() => setShowAbout(true)}
            aria-label="About this map"
          >
            ?
          </button>
        </header>

        {/* The legend explains the map's colours, so it goes when the map does. */}
        {mapUsable && (
          <div className="legend" aria-hidden="true">
            <span className="legend-label">low</span>
            {DEMAND_RAMP.map((color) => (
              <span key={color} className="legend-swatch" style={{ background: color }} />
            ))}
            <span className="legend-label">high</span>
          </div>
        )}
      </div>

      <section className="bottom-sheet">
        <TimeSlider
          moment={activeMoment}
          onChange={handleMomentChange}
          isLive={isLive}
          onGoLive={handleGoLive}
        />

        {selectedCell ? (
          <CellDetail
            cell={selectedCell}
            description={describe(selectedCell.h3)}
            onClear={() => setSelected(null)}
          />
        ) : (
          <>
            <h2 className="section-label">Best places to be</h2>
            <TopCells cells={best} describe={describe} selected={selected} onSelect={setSelected} />
          </>
        )}
      </section>

      {showAbout && <AboutSheet meta={bundle.meta} onClose={dismissAbout} />}
    </div>
  );
}
